/**
 * Overdue Callback Queue — Phase 1c.2
 * Read-only advisory queue derived from the canonical selector.
 * No automation. SLA breach is a flag, not a trigger.
 */
import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAppData } from "@/data/AppDataContext";
import { getOverdueCallbackQueue } from "@/data/contractor/networkExecution";

export function OverdueCallbackQueue() {
  const { callbacks, disputeEvents, holdbackLedger, contractors, jobs } = useAppData();

  const queue = useMemo(
    () =>
      getOverdueCallbackQueue({
        callbacks,
        disputeEvents,
        holdbackLedger,
      }),
    [callbacks, disputeEvents, holdbackLedger],
  );

  if (queue.length === 0) {
    return (
      <Card className="p-3 text-sm text-muted-foreground" data-testid="overdue-queue-empty">
        No overdue callbacks. SLA windows are based on severity (high 24h / medium 72h / low 168h).
      </Card>
    );
  }

  return (
    <Card className="p-0 overflow-hidden" data-testid="overdue-queue">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-xs uppercase">
          <tr>
            <th className="text-left p-2">Callback</th>
            <th className="text-left p-2">Job</th>
            <th className="text-left p-2">Executor</th>
            <th className="text-left p-2">Severity</th>
            <th className="text-right p-2">Hours overdue</th>
            <th className="text-left p-2">Flags</th>
          </tr>
        </thead>
        <tbody>
          {queue.map((row) => {
            const cb = row.callback;
            const job = jobs.find((j) => j.id === cb.jobId);
            const executor = contractors.find(
              (c) => c.id === cb.installerContractorIdAtCallback,
            );
            const sev = cb.severity;
            return (
              <tr key={cb.id} className="border-t">
                <td className="p-2 text-xs">
                  <div className="font-medium">{cb.type}</div>
                  <div className="text-muted-foreground">{cb.id}</div>
                </td>
                <td className="p-2 text-xs">{job?.id ?? cb.jobId}</td>
                <td className="p-2 text-xs">
                  {executor?.companyName ?? cb.installerContractorIdAtCallback ?? "unassigned"}
                </td>
                <td className="p-2">
                  <Badge
                    variant={sev === "high" ? "destructive" : "outline"}
                    className="text-[10px] capitalize"
                  >
                    {sev}
                  </Badge>
                </td>
                <td className="p-2 text-right tabular-nums">
                  {row.hoursOverdue.toFixed(1)}
                </td>
                <td className="p-2 text-xs space-x-1">
                  {row.hasOpenDispute && (
                    <Badge variant="outline" className="text-[10px]">
                      open dispute
                    </Badge>
                  )}
                  {row.hasActiveHoldback && (
                    <Badge variant="outline" className="text-[10px]">
                      active holdback
                    </Badge>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-3 py-2 text-[11px] text-muted-foreground border-t">
        Advisory list. Escalation requires manual action — soft mode never auto-blocks.
      </div>
    </Card>
  );
}
